/** Max ký tự ô nhập / lưu SĐT VN (khớp backend ClientContactRules). */
export const PHONE_INPUT_MAX_LENGTH = 30

/**
 * SĐT di động / cố định VN: 03|05|07|08|09 hoặc 012|016|018|019 + 8 số.
 * (Sửa `[2|6|8|9]` → `[2689]`, bỏ quantifier `+` thừa trên prefix.)
 */
export const VIETNAMESE_PHONE_REGEX = /^(03|05|07|08|09|01[2689])[0-9]{8}$/

export function toNationalPhoneDigits(value: string): string {
  let digits = value.replace(/\D+/g, '')
  if (digits.startsWith('84')) {
    digits = `0${digits.slice(2)}`
  } else if (!digits.startsWith('0') && digits.length === 9) {
    digits = `0${digits}`
  }
  return digits
}

/**
 * Normalize VN phone to E.164 (+84…). Mirrors backend PhoneNormalizer.
 * Returns null when the value cannot be normalized (or is already masked).
 */
export function toE164Phone(value: string | null | undefined): string | null {
  if (value == null) return null
  const raw = String(value).trim()
  if (!raw || raw.includes('*') || raw.includes('•')) return null

  const digits = raw.replace(/[^\d+]/g, '')
  if (!digits) return null

  let rest: string
  if (digits.startsWith('+84')) {
    rest = digits.slice(3)
  } else if (digits.startsWith('84')) {
    rest = digits.slice(2)
  } else if (digits.startsWith('0')) {
    rest = digits.slice(1)
  } else {
    return null
  }

  if (!/^\d{9,10}$/.test(rest)) return null
  return `+84${rest}`
}

/** +84, middle hidden, last 3 digits. Same shape as PhoneNormalizer::mask. */
export function maskPhoneDisplay(value: string | null | undefined): string | null {
  if (value == null) return null
  const raw = String(value).trim()
  if (!raw || raw === '—' || raw === '-') return null
  if (raw.startsWith('+84') && raw.includes('•')) return raw

  const e164 = toE164Phone(raw)
  if (e164) {
    if (e164.length <= 4) return e164
    const hidden = Math.max(0, e164.length - 6)
    return `${e164.slice(0, 3)}${'•'.repeat(hidden)}${e164.slice(-3)}`
  }

  const tail = raw.replace(/\D/g, '').slice(-3)
  if ((raw.includes('*') || raw.includes('•')) && tail.length === 3) {
    return `+84${'•'.repeat(5)}${tail}`
  }

  return null
}

/** Display helper: +84•••••123. Keeps an existing bullet mask. */
export function formatPhoneDisplay(value: string | null | undefined, empty = '—'): string {
  if (value == null) return empty
  const raw = String(value).trim()
  if (!raw || raw === '—' || raw === '-') return empty
  return maskPhoneDisplay(raw) ?? raw
}

/** Display helper: national "0" leading (vd 0977587729); keep masks; fall back to original trim. */
export function formatPhoneNational(value: string | null | undefined, empty = '—'): string {
  if (value == null) return empty
  const raw = String(value).trim()
  if (!raw || raw === '—' || raw === '-') return empty
  if (raw.includes('*') || raw.includes('•')) return raw
  return toNationalPhoneDigits(raw) || raw
}

/** Strip +84 / leading 0 for fuzzy phone search. */
export function phoneSearchCore(value: string): string {
  let digits = String(value).replace(/\D/g, '')
  if (digits.startsWith('84')) {
    digits = digits.slice(2)
  }
  return digits.replace(/^0+/, '')
}

/** Substring match for worker search — 0xxx, +84, 84xxx, and partial digits. */
export function phoneMatchesSearch(keyword: string, phone: string | null | undefined): boolean {
  const raw = String(phone ?? '').trim()
  if (!raw || raw === '—' || raw === '-') return false

  const term = String(keyword ?? '').trim()
  if (!term) return true

  const lowerTerm = term.toLowerCase()
  if (raw.toLowerCase().includes(lowerTerm)) return true

  const displayPhone = formatPhoneDisplay(raw, '')
  if (displayPhone && displayPhone.toLowerCase().includes(lowerTerm)) return true

  const digitTerm = term.replace(/\D/g, '')
  if (digitTerm.length < 3) return false

  if (raw.includes('*') || raw.includes('•')) {
    const visibleDigits = raw.replace(/\D/g, '')
    const termCore = phoneSearchCore(term)
    return visibleDigits.includes(digitTerm)
      || (termCore.length >= 3 && phoneSearchCore(visibleDigits).includes(termCore))
  }

  const phoneCore = phoneSearchCore(raw)
  const termCore = phoneSearchCore(term)
  if (termCore.length >= 3 && phoneCore.includes(termCore)) return true

  const phoneDigits = raw.replace(/\D/g, '')
  if (phoneDigits.includes(digitTerm)) return true

  const phoneNational = toNationalPhoneDigits(raw)
  const termNational = toNationalPhoneDigits(digitTerm)
  return Boolean(termNational && phoneNational.includes(termNational))
}

export function isVietnamesePhoneNumber(value: string | undefined): boolean {
  if (!value?.trim()) return false
  return VIETNAMESE_PHONE_REGEX.test(toNationalPhoneDigits(value))
}

export function normalizeVietnamesePhoneInput(value: string): string | null {
  const digits = toNationalPhoneDigits(value)
  if (!digits || !VIETNAMESE_PHONE_REGEX.test(digits)) return null
  return digits
}

/** @alias isVietnamesePhoneNumber */
export function isValidVietnamesePhone(value: string | undefined): boolean {
  return isVietnamesePhoneNumber(value)
}

export function isPhoneInputWithinMaxLength(value: string): boolean {
  return value.trim().length <= PHONE_INPUT_MAX_LENGTH
}
